import { FireIcon } from "@/components/conversations/activities/connect-moment/fire-icon";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { Scribbles } from "@somethings-ui/core/assets";
import type {
  HotTakeAnswer,
  HotTakesConfigStatement,
} from "@somethings-ui/core/hot-takes";
import { ImageOff, FileX, RefreshCwOff } from "lucide-react";

import { HotTakesSummaryCard } from "./hot-takes-summary-card";

type ThumbnailSize = "sm" | "md";

const sizeConfig = {
  sm: {
    container: "h-7 w-9",
    layer: "h-6 w-6",
    rounded: "rounded",
  },
  md: {
    container: "h-14 w-[72px]",
    layer: "h-12 w-12",
    rounded: "rounded-lg",
  },
};

interface MomentThumbnailProps {
  previewUrl?: string | null;
  size?: ThumbnailSize;
}

export function MomentThumbnail({
  previewUrl,
  size = "md",
}: MomentThumbnailProps) {
  const { url: signedUrl } = useSignedMedia(previewUrl ?? undefined);
  const config = sizeConfig[size];

  return (
    <div className={`relative ${config.container} shrink-0`}>
      <div
        className={`absolute left-0 ${size === "sm" ? "top-0.5" : "top-1"} ${config.layer} ${config.rounded} bg-zinc-800`}
        style={{ transform: "rotate(-12deg)" }}
      />
      <div
        className={`absolute ${size === "sm" ? "left-1" : "left-2"} top-0 ${config.layer} ${config.rounded} bg-teal-700`}
        style={{ transform: "rotate(-6deg)" }}
      />
      <div
        className={`absolute right-0 top-0 ${config.layer} overflow-hidden ${config.rounded} bg-zinc-200 shadow-sm`}
      >
        {signedUrl ? (
          <img
            src={signedUrl}
            alt="Moment thumbnail"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-zinc-300" />
        )}
      </div>
    </div>
  );
}

interface NoteThumbnailProps {
  noteContent?: string | null;
  size?: ThumbnailSize;
}

export function NoteThumbnail({ noteContent, size = "md" }: NoteThumbnailProps) {
  const config = sizeConfig[size];

  if (size === "sm") {
    return (
      <div
        className={`${config.layer} shrink-0 ${config.rounded} bg-stone-100 shadow-sm`}
        style={{ transform: "rotate(-4deg)" }}
      />
    );
  }

  return (
    <div className="relative h-14 w-14 shrink-0">
      <div
        className={`absolute left-0 top-1 ${config.layer} ${config.rounded} bg-stone-200`}
        style={{ transform: "rotate(-6deg)" }}
      />
      <div
        className={`absolute right-0 top-0 ${config.layer} overflow-hidden ${config.rounded} bg-stone-100 p-1.5 shadow-sm`}
        style={{ transform: "rotate(-2deg)" }}
      >
        {noteContent ? (
          <p className="line-clamp-4 text-[6px] leading-tight text-stone-500">
            {noteContent}
          </p>
        ) : null}
      </div>
    </div>
  );
}

interface SwapThumbnailProps {
  previewUrl?: string | null;
  secondPreviewUrl?: string | null;
  size?: ThumbnailSize;
}

export function SwapThumbnail({ previewUrl, secondPreviewUrl, size = "md" }: SwapThumbnailProps) {
  const { url: signedUrl } = useSignedMedia(previewUrl ?? undefined);
  const { url: signedSecondUrl } = useSignedMedia(secondPreviewUrl ?? undefined);
  const config = sizeConfig[size];

  const hasBothImages = signedUrl && signedSecondUrl;

  return (
    <div className={`relative ${config.container} shrink-0`}>
      {/* Left photo - tilts left like view-swap-view */}
      <div
        className={`absolute left-0 ${size === "sm" ? "top-0.5" : "top-1"} ${config.layer} overflow-hidden ${config.rounded} ${hasBothImages ? "bg-zinc-200 shadow-sm" : "border-2 border-dashed border-zinc-300 bg-zinc-100"}`}
        style={{ transform: "rotate(-8deg)" }}
      >
        {signedSecondUrl ? (
          <img
            src={signedSecondUrl}
            alt="Swap thumbnail"
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      {/* Right photo - tilts right like view-swap-view */}
      <div
        className={`absolute right-0 top-0 ${config.layer} overflow-hidden ${config.rounded} bg-zinc-200 shadow-sm`}
        style={{ transform: "rotate(4deg)" }}
      >
        {signedUrl ? (
          <img
            src={signedUrl}
            alt="Swap thumbnail"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-zinc-300" />
        )}
      </div>
    </div>
  );
}

// Unavailable state thumbnails
interface UnavailableThumbnailProps {
  size?: ThumbnailSize;
}

export function MomentUnavailableThumbnail({ size = "md" }: UnavailableThumbnailProps) {
  const config = sizeConfig[size];

  return (
    <div className={`relative ${config.container} shrink-0`}>
      <div
        className={`absolute left-0 ${size === "sm" ? "top-0.5" : "top-1"} ${config.layer} ${config.rounded} bg-zinc-300`}
        style={{ transform: "rotate(-12deg)" }}
      />
      <div
        className={`absolute ${size === "sm" ? "left-1" : "left-2"} top-0 ${config.layer} ${config.rounded} bg-zinc-400`}
        style={{ transform: "rotate(-6deg)" }}
      />
      <div
        className={`absolute right-0 top-0 ${config.layer} ${config.rounded} flex flex-col items-center justify-center bg-red-50 shadow-sm`}
      >
        <ImageOff size={size === "sm" ? 8 : 12} className="text-red-400" />
        <span className={`font-medium text-red-400 ${size === "sm" ? "text-[4px]" : "text-[6px]"}`}>
          unavailable
        </span>
      </div>
    </div>
  );
}

export function NoteUnavailableThumbnail({ size = "md" }: UnavailableThumbnailProps) {
  const config = sizeConfig[size];

  if (size === "sm") {
    return (
      <div
        className={`${config.layer} shrink-0 ${config.rounded} flex flex-col items-center justify-center bg-red-50 shadow-sm`}
        style={{ transform: "rotate(-4deg)" }}
      >
        <FileX size={8} className="text-red-400" />
        <span className="text-[4px] font-medium text-red-400">unavailable</span>
      </div>
    );
  }

  return (
    <div className="relative h-14 w-14 shrink-0">
      <div
        className={`absolute left-0 top-1 ${config.layer} ${config.rounded} bg-stone-300`}
        style={{ transform: "rotate(-6deg)" }}
      />
      <div
        className={`absolute right-0 top-0 ${config.layer} ${config.rounded} flex flex-col items-center justify-center bg-red-50 shadow-sm`}
        style={{ transform: "rotate(-2deg)" }}
      >
        <FileX size={12} className="text-red-400" />
        <span className="text-[6px] font-medium text-red-400">unavailable</span>
      </div>
    </div>
  );
}

export function SwapUnavailableThumbnail({ size = "md" }: UnavailableThumbnailProps) {
  const config = sizeConfig[size];

  return (
    <div className={`relative ${config.container} shrink-0`}>
      <div
        className={`absolute left-0 ${size === "sm" ? "top-0.5" : "top-1"} ${config.layer} ${config.rounded} bg-zinc-300`}
        style={{ transform: "rotate(-8deg)" }}
      />
      <div
        className={`absolute right-0 top-0 ${config.layer} ${config.rounded} flex flex-col items-center justify-center bg-red-50 shadow-sm`}
        style={{ transform: "rotate(4deg)" }}
      >
        <RefreshCwOff size={size === "sm" ? 8 : 12} className="text-red-400" />
        <span className={`font-medium text-red-400 ${size === "sm" ? "text-[4px]" : "text-[6px]"}`}>
          unavailable
        </span>
      </div>
    </div>
  );
}

interface HotTakesThumbnailProps {
  size?: ThumbnailSize;
  mentorAvatarUrl?: string | null;
  teenAvatarUrl?: string | null;
  teenName?: string | null;
  matchesCount?: number;
}

// Chat-timeline mini preview of the Hot Takes Summary card. Purple gradient
// tile: title, subtitle "You & X agree on N takes", stacked avatars.
export function HotTakesThumbnail({
  mentorAvatarUrl,
  teenAvatarUrl,
  teenName,
  matchesCount,
}: HotTakesThumbnailProps) {
  const signedMentor = useSignedMedia(mentorAvatarUrl ?? undefined);
  const signedTeen = useSignedMedia(teenAvatarUrl ?? undefined);
  const n = matchesCount ?? 0;

  return (
    <div
      className="relative flex h-[170px] w-[156px] shrink-0 flex-col items-center justify-center gap-3 overflow-hidden rounded-[16px] px-3 py-4 shadow-sm"
      style={{
        backgroundImage:
          "linear-gradient(180deg, #322964 0%, #1D1F46 100%)",
      }}
    >
      <img
        src={Scribbles.purpleCloudTilted}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-0 w-full object-contain"
      />

      <div className="relative flex flex-col items-center gap-0.5 text-center">
        <span className="text-[18px] font-bold leading-[1.05] tracking-tight text-white">
          Summary
        </span>
        {teenName && (
          <span className="text-[10px] leading-tight text-white/90">
            You &amp; {teenName} agree on {n} take{n === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Stacked avatars */}
      <div className="relative flex items-center">
        <div className="h-7 w-7 overflow-hidden rounded-full border border-white/40 bg-white/10">
          {signedMentor.url ? (
            <img
              src={signedMentor.url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
        <div className="-ml-2 h-7 w-7 overflow-hidden rounded-full border border-white/40 bg-white/10">
          {signedTeen.url ? (
            <img
              src={signedTeen.url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface HotTakesStatementThumbnailProps {
  statement: HotTakesConfigStatement;
  teenAnswer: HotTakeAnswer | null;
  mentorAvatarUrl?: string | null;
  teenAvatarUrl?: string | null;
}

// Mini 114×170 statement-card thumbnail used when a reaction/comment is
// scoped to a specific take.
export function HotTakesStatementThumbnail({
  statement,
  teenAnswer,
  mentorAvatarUrl,
  teenAvatarUrl,
}: HotTakesStatementThumbnailProps) {
  const mentor = useSignedMedia(mentorAvatarUrl ?? undefined);
  const teen = useSignedMedia(teenAvatarUrl ?? undefined);

  const disagreeAvatars: string[] = [
    ...(statement.mentorAnswer === "disagree" && mentor.url
      ? [mentor.url]
      : []),
    ...(teenAnswer === "disagree" && teen.url ? [teen.url] : []),
  ];
  const agreeAvatars: string[] = [
    ...(statement.mentorAnswer === "agree" && mentor.url
      ? [mentor.url]
      : []),
    ...(teenAnswer === "agree" && teen.url ? [teen.url] : []),
  ];

  return (
    <div
      className="relative h-[170px] w-[114px] shrink-0 overflow-hidden rounded-[16px] shadow-sm"
      style={{
        backgroundImage:
          "linear-gradient(180deg, #322964 0%, #1D1F46 100%)",
      }}
    >
      <img
        src={Scribbles.purpleCloudTilted}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-0 w-full object-contain"
      />

      <div className="absolute inset-0 flex items-center justify-center px-2">
        <p
          className="break-words text-center font-bold leading-[1.1] tracking-tight text-white"
          style={{ fontSize: 11.5, maxWidth: 94, letterSpacing: -0.28 }}
        >
          {statement.text}
        </p>
      </div>

      <div
        className="absolute left-0 right-0 flex items-end justify-center"
        style={{ bottom: 14, gap: 5 }}
      >
        <PillWithAvatars label="disagree" side="left" avatars={disagreeAvatars} />
        <PillWithAvatars label="agree" side="right" avatars={agreeAvatars} />
      </div>
    </div>
  );
}

function PillWithAvatars({
  label,
  side,
  avatars,
}: {
  label: string;
  side: "left" | "right";
  avatars: string[];
}) {
  const tilt = side === "left" ? -10.82 : 14.77;
  return (
    <div className="flex flex-col items-center" style={{ height: 30 }}>
      <div
        className="flex items-center"
        style={{ height: 17, marginBottom: -2 }}
      >
        {avatars.map((url, i) => (
          <div
            key={`${url}-${i}`}
            className="h-[15px] w-[15px] overflow-hidden rounded-full border border-white/40 bg-white/10"
            style={{
              transform: `rotate(${tilt}deg)`,
              marginLeft: i > 0 ? -5 : 0,
              zIndex: avatars.length - i,
            }}
          >
            <img src={url} alt="" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
      <div
        className="flex items-center rounded-[10px] border border-white"
        style={{ paddingInline: 5, paddingBlock: 2 }}
      >
        <span
          className="font-bold text-white"
          style={{ fontSize: 6.5, lineHeight: 1 }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

interface HotTakesSummaryThumbnailProps {
  statements: HotTakesConfigStatement[];
  teenAnswerById: Map<string, HotTakeAnswer>;
  matchesCount: number;
  teenName: string;
  mentorAvatarUrl?: string | null;
  teenAvatarUrl?: string | null;
}

// 114×170 mini of the full HotTakesSummaryCard — scales the real card down
// so the mini matches the real thing exactly.
export function HotTakesSummaryThumbnail({
  statements,
  teenAnswerById,
  matchesCount,
  teenName,
  mentorAvatarUrl,
  teenAvatarUrl,
}: HotTakesSummaryThumbnailProps) {
  const CARD_W = 114;
  const CARD_H = 170;
  const FULL_W = 361;
  const SCALE = CARD_W / FULL_W;
  const FULL_H = CARD_H / SCALE;

  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-[16px] shadow-sm"
      style={{ width: CARD_W, height: CARD_H }}
    >
      <div
        style={{
          width: FULL_W,
          height: FULL_H,
          transform: `scale(${SCALE})`,
          transformOrigin: "top left",
        }}
      >
        <HotTakesSummaryCard
          teenName={teenName}
          matchesCount={matchesCount}
          mentorAvatarUrl={mentorAvatarUrl ?? null}
          teenAvatarUrl={teenAvatarUrl ?? null}
          statements={statements}
          teenAnswerById={teenAnswerById}
        />
      </div>
    </div>
  );
}

export function HotTakesUnavailableThumbnail({
  size = "md",
}: UnavailableThumbnailProps) {
  const config = sizeConfig[size];
  return (
    <div className={`relative ${config.container} shrink-0`}>
      <div
        className={`absolute right-0 top-0 ${config.layer} ${config.rounded} flex flex-col items-center justify-center bg-red-50 shadow-sm`}
        style={{ transform: "rotate(-4deg)" }}
      >
        <FileX size={size === "sm" ? 8 : 12} className="text-red-400" />
      </div>
    </div>
  );
}

interface ConnectMomentLevelThumbnailProps {
  level: 0 | 1 | 2;
  questionText: string | undefined;
  mentorName: string;
  mentorAvatarUrl?: string | null;
  teenName: string;
  teenAvatarUrl?: string | null;
}

export function ConnectMomentLevelThumbnail({
  level,
  questionText,
  mentorName,
  mentorAvatarUrl,
  teenName,
  teenAvatarUrl,
}: ConnectMomentLevelThumbnailProps) {
  return (
    <div
      className="relative h-[170px] w-[114px] shrink-0 overflow-hidden rounded-[16px] shadow-sm"
      style={{ backgroundColor: "#2F2D82" }}
    >
      <img
        src={Scribbles.purpleCloudTilted}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-0 w-full object-contain"
      />

      <div className="relative flex h-full flex-col">
        <div className="flex items-center justify-center pt-3">
          <span
            className="text-white"
            style={{ fontSize: 9, letterSpacing: -0.18 }}
          >
            Level {level + 1}
          </span>
        </div>

        <div className="flex flex-1 items-center justify-center px-2">
          <p
            className="break-words text-center font-bold leading-[1.1] tracking-tight text-white"
            style={{ fontSize: 11.5, maxWidth: 94, letterSpacing: -0.28 }}
          >
            {questionText ?? "…"}
          </p>
        </div>

        <div className="flex items-center justify-center pb-3">
          <div className="-mr-[6px]">
            <UserAvatar
              imageUrl={mentorAvatarUrl ?? undefined}
              name={mentorName}
              size={20}
              showBorder
            />
          </div>
          <UserAvatar
            imageUrl={teenAvatarUrl ?? undefined}
            name={teenName}
            size={20}
            showBorder
          />
        </div>
      </div>
    </div>
  );
}

interface ConnectMomentSummaryThumbnailProps {
  mentorName: string;
  mentorAvatarUrl?: string | null;
  teenName: string;
  teenAvatarUrl?: string | null;
}

export function ConnectMomentSummaryThumbnail({
  mentorName,
  mentorAvatarUrl,
  teenName,
  teenAvatarUrl,
}: ConnectMomentSummaryThumbnailProps) {
  return (
    <div
      className="relative h-[170px] w-[114px] shrink-0 overflow-hidden rounded-[16px] shadow-sm"
      style={{ backgroundColor: "#2F2D82" }}
    >
      <img
        src={Scribbles.purpleCloudTilted}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-0 w-full object-contain"
      />

      <div className="relative flex h-full flex-col items-center justify-center gap-3 px-2">
        <div className="flex flex-row items-center justify-center">
          <UserAvatar
            imageUrl={mentorAvatarUrl ?? undefined}
            name={mentorName}
            size={38}
            showBorder
          />
          <div style={{ marginLeft: -14 }}>
            <UserAvatar
              imageUrl={teenAvatarUrl ?? undefined}
              name={teenName}
              size={38}
              showBorder
            />
          </div>
        </div>

        <div className="flex flex-row items-center justify-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <FireIcon key={i} size={14} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function ConnectMomentUnavailableThumbnail({
  size = "md",
}: UnavailableThumbnailProps) {
  const config = sizeConfig[size];
  return (
    <div className={`relative ${config.container} shrink-0`}>
      <div
        className={`absolute right-0 top-0 ${config.layer} ${config.rounded} flex flex-col items-center justify-center bg-red-50 shadow-sm`}
        style={{ transform: "rotate(-4deg)" }}
      >
        <FileX size={size === "sm" ? 8 : 12} className="text-red-400" />
      </div>
    </div>
  );
}
